"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const events_1 = require("../constants/events");
/**
 * Returns true if the given value is a number.
 * @private
 */
const isNumber = val => typeof val === 'number';
/**
 * Returns true if the browser supports the Performance Timing API.
 * @todo Handle top-level typeof window === `undefined`, e.g., in node
 *   environment (PERF-802)
 * @private
 */
const isPerformanceTimingSupported = () => { var _a; return typeof window !== 'undefined' && ((_a = window.performance) === null || _a === void 0 ? void 0 : _a.timing); };
/**
 * Get metrics we care about derived from the navigation performance API, e.g.,
 * TTFB (time to first byte). Once the class is instantiated it waits for the
 * window's onLoad event before fetching the timings.
 *
 * @see
 * https://www.w3.org/TR/navigation-timing/#sec-window.performance-attribute
 */
class NavigationTimings {
    constructor() {
        /**
         * Return how long DOMContentLoaded event took to fire from the
         * `connectStart` time. Return {} if it cannot be calculated yet (or ever).
         */
        this.getDOMContentLoadedTime = () => {
            // Bail if performance timing API is not supported
            if (!isPerformanceTimingSupported())
                return {};
            const { connectStart, domContentLoadedEventEnd } = window.performance.timing;
            if (isNumber(connectStart) && isNumber(domContentLoadedEventEnd)) {
                const DOMContentLoadedTime = Math.round(domContentLoadedEventEnd - connectStart);
                if (DOMContentLoadedTime >= 0) {
                    this.hasMetricBeenReturned[events_1.EVENT_NAMES.DOM_CONTENT_LOADED] = true;
                    return {
                        [events_1.EVENT_NAMES.DOM_CONTENT_LOADED]: DOMContentLoadedTime,
                    };
                }
            }
            return {};
        };
        /**
         * Same as `getDOMContentLoadedTime()`, but only returns the value once. If
         * called again, return {}
         */
        this.getDOMContentLoadedTimeOnce = () => {
            if (!this.hasMetricBeenReturned[events_1.EVENT_NAMES.DOM_CONTENT_LOADED]) {
                return this.getDOMContentLoadedTime();
            }
            return {};
        };
        // <PERF-777> Keep the legacy TTFB measurement implementations so we can
        // compare the old and new values before replacing them.
        // TODO: Remove these methods after PERF-777 is complete. Consider renaming
        // module to DOMContentLoaded.js
        /**
         * Return the time to first byte (TTFB) from the `requestStart` time, or {}
         * if it cannot be calculated yet (or ever).
         * @deprecated Use WebVitalsMetrics.TTFB() instead
         */
        this.getTimeToFirstByte = () => {
            // Don't accept TTFB > 10 minutes, or < 0 milliseconds. If outside of
            // those bounds, assume the browser is reporting invalid timing metrics.
            const maxValidTTFB = 600000;
            const minValidTTFB = 0;
            // Bail if performance timing API is not supported
            if (!isPerformanceTimingSupported())
                return {};
            const { requestStart, responseEnd } = window.performance.timing;
            const rawTTFB = responseEnd - requestStart;
            if (isNumber(rawTTFB) &&
                rawTTFB >= minValidTTFB &&
                rawTTFB <= maxValidTTFB &&
                // requestStart/responseEnd values should be epocs, so they
                // should never be 0
                requestStart > 0 &&
                responseEnd > 0) {
                this.hasMetricBeenReturned[events_1.EVENT_NAMES.TTFB] = true;
                return { [events_1.EVENT_NAMES.TTFB]: Math.round(rawTTFB) };
            }
            return {};
        };
        /**
         * Same as `getTimeToFirstByte()`, but only returns the value once. If
         * called again, return {}
         * @deprecated Use WebVitalsMetrics.TTFB() instead
         */
        this.getTimeToFirstByteOnce = () => {
            if (!this.hasMetricBeenReturned[events_1.EVENT_NAMES.TTFB]) {
                return this.getTimeToFirstByte();
            }
            return {};
        };
        // Track which metrics have been measured to avoid reporting them
        // more than once.
        this.hasMetricBeenReturned = {
            [events_1.EVENT_NAMES.DOM_CONTENT_LOADED]: false,
            [events_1.EVENT_NAMES.TTFB]: false,
        };
    }
}
exports.default = NavigationTimings;
